import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useI18n } from "@/context/I18nContext";
import { API } from "@/context/AuthContext";
import { DASHBOARD } from "@/constants/testIds";
import { priorityStyle } from "@/lib/taskPriority";
import TaskDetailsModal from "@/components/TaskDetailsModal";
import OrderDetailsModal from "@/components/OrderDetailsModal";

// ---- Date helpers (local, ISO YYYY-MM-DD) ----
const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function buildMonthGrid(year, month /* 1-12 */) {
  // 6-row x 7-col grid, Monday-first
  const first = new Date(year, month - 1, 1);
  // JS getDay(): 0=Sun..6=Sat → convert to Mon=0..Sun=6
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month - 1, 1 - offset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({
      date: d,
      iso: isoOf(d),
      inMonth: d.getMonth() === month - 1,
    });
  }
  return cells;
}

const WEEKDAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const WEEKDAYS_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ---- Draggable task chip ----
function TaskChip({ task, onClickTask }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task-${task.id}`,
    data: { taskId: task.id },
  });
  const bg = task.overdue ? "#FF3B30" : task.color || "#94A3B8";

  // Detect tap-vs-drag: pointer down position; if pointer up without significant movement, treat as click.
  // IMPORTANT: We compose with dnd-kit's listeners (which include onPointerDown for drag activation).
  // Overriding them would break drag-and-drop, so call them through.
  const downRef = React.useRef(null);
  const onPointerDown = (e) => {
    downRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    if (listeners && typeof listeners.onPointerDown === "function") {
      listeners.onPointerDown(e);
    }
  };
  const onPointerUp = (e) => {
    const d = downRef.current;
    downRef.current = null;
    if (listeners && typeof listeners.onPointerUp === "function") {
      listeners.onPointerUp(e);
    }
    if (!d || isDragging) return;
    const dx = Math.abs(e.clientX - d.x);
    const dy = Math.abs(e.clientY - d.y);
    const dt = Date.now() - d.t;
    if (dx < 4 && dy < 4 && dt < 600) {
      e.stopPropagation();
      onClickTask && onClickTask(task);
    }
  };

  // Spread remaining listeners (e.g. onKeyDown) but ensure our composed handlers win.
  const { onPointerDown: _odp, onPointerUp: _oup, ...restListeners } = listeners || {};

  // Priority indicator: thick coloured border + dot prefix on the chip
  const prio = task.priority || "normal";
  const ps = priorityStyle(prio);
  const priorityClass = prio !== "normal" ? ps.calendarBorder : (task.overdue ? "border-red-700" : "border-black/10");

  return (
    <div
      ref={setNodeRef}
      data-testid={DASHBOARD.calendarTask(task.id)}
      {...restListeners}
      {...attributes}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      title={`${task.name}${task.employee_name ? " · " + task.employee_name : ""}${prio !== "normal" ? " · " + (prio === "urgent" ? "URGENTE" : "PRIORITARIA") : ""}`}
      className={`select-none cursor-pointer text-[10px] font-display font-bold tracking-tight px-1.5 py-1 truncate border-2 ${priorityClass} ${isDragging ? "opacity-50 cursor-grabbing" : ""} ${task.status === "completed" ? "line-through opacity-70" : ""} flex items-center gap-1`}
      style={{ background: bg, color: "#fff" }}
    >
      {prio === "urgent" && (
        <span
          data-testid={DASHBOARD.taskPriorityBadge(task.id)}
          className="inline-flex items-center justify-center bg-white text-red-700 font-black text-[9px] leading-none px-1 py-[1px] tracking-tighter"
        >
          !!
        </span>
      )}
      {prio === "priority" && (
        <span
          data-testid={DASHBOARD.taskPriorityBadge(task.id)}
          className="inline-flex items-center justify-center bg-amber-300 text-black font-black text-[9px] leading-none px-1 py-[1px] tracking-tighter"
        >
          !
        </span>
      )}
      <span className="truncate">{task.name}</span>
    </div>
  );
}

// ---- Droppable day cell ----
function DayCell({ iso, inMonth, isToday, dayNum, tasks, onClickTask }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `day-${iso}`,
    data: { iso },
  });
  return (
    <div
      ref={setNodeRef}
      data-testid={DASHBOARD.calendarDay(iso)}
      className={`min-h-[110px] border-r border-b border-[var(--border)] p-1.5 flex flex-col gap-1 transition-colors ${
        inMonth ? "bg-white" : "bg-[var(--surface-alt,#f5f5f5)] opacity-60"
      } ${isOver ? "bg-yellow-50 ring-2 ring-amber-400 ring-inset" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`font-mono-tech text-[11px] tabular-nums ${
            isToday
              ? "bg-black text-white font-black px-1.5 py-0.5 rounded-sm"
              : "text-[var(--text-secondary)]"
          }`}
        >
          {dayNum}
        </span>
        {tasks.length > 0 && (
          <span className="font-mono-tech text-[9px] text-[var(--text-secondary)]">
            {tasks.length}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-0.5 overflow-hidden">
        {tasks.map((t) => (
          <TaskChip key={t.id} task={t} onClickTask={onClickTask} />
        ))}
      </div>
    </div>
  );
}

export default function CalendarPanel({ onOpenPricingRule }) {
  const { t, lang } = useI18n();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [data, setData] = useState({ employees: [], tasks: [] });
  const [loading, setLoading] = useState(true);

  // Task details / order details modals
  const [taskDetails, setTaskDetails] = useState(null);
  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);

  const todayIso = isoOf(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tasks/calendar`, {
        params: { year, month },
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const cells = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const tasksByDate = useMemo(() => {
    const map = {};
    for (const tk of data.tasks || []) {
      const key = (tk.due_date || "").slice(0, 10);
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(tk);
    }
    return map;
  }, [data.tasks]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;
    const taskId = active?.data?.current?.taskId;
    const newIso = over?.data?.current?.iso;
    if (!taskId || !newIso) return;
    const task = (data.tasks || []).find((tk) => tk.id === taskId);
    if (!task) return;
    if ((task.due_date || "").slice(0, 10) === newIso) return;

    // Optimistic update
    const previous = data;
    setData((d) => ({
      ...d,
      tasks: d.tasks.map((tk) => (tk.id === taskId ? { ...tk, due_date: newIso } : tk)),
    }));

    try {
      await axios.patch(`${API}/tasks/${taskId}/reschedule`, { due_date: newIso });
      toast.success(t("reschedule_success"));
      // Refresh in case task moved out of month
      load();
    } catch (e) {
      setData(previous);
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const goPrev = () => {
    if (month === 1) {
      setYear(year - 1);
      setMonth(12);
    } else {
      setMonth(month - 1);
    }
  };
  const goNext = () => {
    if (month === 12) {
      setYear(year + 1);
      setMonth(1);
    } else {
      setMonth(month + 1);
    }
  };
  const goToday = () => {
    const n = new Date();
    setYear(n.getFullYear());
    setMonth(n.getMonth() + 1);
  };

  const weekdays = lang === "en" ? WEEKDAYS_EN : WEEKDAYS_ES;
  const monthName = (lang === "en" ? MONTHS_EN : MONTHS_ES)[month - 1];

  // Measure the app header height so the sticky weekday row sits right below it
  const [headerOffset, setHeaderOffset] = useState(64);
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector("header");
      if (header) {
        const h = Math.round(header.getBoundingClientRect().height);
        if (h > 0) setHeaderOffset(h);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <div data-testid={DASHBOARD.calendarView} className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">CAL / 01</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {t("calendar_title")}
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">{t("calendar_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={DASHBOARD.calendarPrev}
            onClick={goPrev}
            className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
            aria-label={t("prev_month")}
          >
            ‹
          </button>
          <button
            data-testid={DASHBOARD.calendarToday}
            onClick={goToday}
            className="border border-[var(--border)] px-3 py-1.5 text-sm font-display font-bold hover:bg-black hover:text-white"
          >
            {t("today_btn")}
          </button>
          <button
            data-testid={DASHBOARD.calendarNext}
            onClick={goNext}
            className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
            aria-label={t("next_month")}
          >
            ›
          </button>
          <div className="font-display font-black text-xl tracking-tight ml-2 tabular-nums">
            {monthName} {year}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3" style={{ background: "#FF3B30" }} />
          <span>{t("legend_overdue")}</span>
        </div>
        {(data.employees || []).map((e) => (
          <div key={e.id} className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3" style={{ background: e.color }} />
            <span className="font-display">{e.name}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3" style={{ background: "#94A3B8" }} />
          <span>{t("legend_unassigned")}</span>
        </div>
      </div>

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="border-l border-t border-[var(--border)] bg-white">
            {/* Weekday header (sticky while scrolling) */}
            <div
              data-testid="calendar-weekday-header"
              className="grid grid-cols-7 sticky z-40 bg-white shadow-[0_1px_0_0_var(--border)]"
              style={{ top: `${headerOffset}px` }}
            >
              {weekdays.map((w) => (
                <div
                  key={w}
                  className="border-r border-b border-[var(--border)] px-2 py-2 label-overline bg-[var(--surface-alt,#fafafa)]"
                >
                  {w}
                </div>
              ))}
            </div>
            {/* Day grid */}
            <div className="grid grid-cols-7">
              {cells.map((c) => (
                <DayCell
                  key={c.iso}
                  iso={c.iso}
                  inMonth={c.inMonth}
                  isToday={c.iso === todayIso}
                  dayNum={c.date.getDate()}
                  tasks={tasksByDate[c.iso] || []}
                  onClickTask={(tk) => {
                    setTaskDetails(tk);
                    setTaskDetailsOpen(true);
                  }}
                />
              ))}
            </div>
          </div>
        </DndContext>
      )}

      <TaskDetailsModal
        open={taskDetailsOpen}
        task={taskDetails}
        onClose={() => {
          setTaskDetailsOpen(false);
          setTaskDetails(null);
        }}
        onOpenOrder={(o) => {
          setTaskDetailsOpen(false);
          setOrderDetails(o);
          setOrderDetailsOpen(true);
        }}
        onCompleted={(updated) => {
          setData((d) => ({
            ...d,
            tasks: d.tasks.map((tk) =>
              tk.id === updated.id ? { ...tk, status: "completed", overdue: false } : tk
            ),
          }));
        }}
      />

      <OrderDetailsModal
        open={orderDetailsOpen}
        order={orderDetails}
        onOpenPricingRule={onOpenPricingRule}
        onClose={() => {
          setOrderDetailsOpen(false);
          setOrderDetails(null);
        }}
      />
    </div>
  );
}
